// ═══════════════════════════════════════════════════════════════
//  难度 / 平衡参数（集中管理）
//  改难度只需要改这里，不用碰场景逻辑。
//
//  设计目标：让「无限刷」成立 ——
//   1) 敌人数量线性增长但软上限，切断无限膨胀（性能与可玩性）
//   2) 地图硬夹取到 ~1.75 屏（56×32），4~5 层封顶，不再无限变大（找门不用靠运气）
//   3) 墙壁数量封顶，不再线性堆到 50%+（不变成纯迷宫）
//   4) 敌人 HP 对数增长 + 伤害封顶 → 高层不会无限膨胀
//   5) 58 层后引入怪物词缀 → 保持新鲜感
//   6) 天赋槽随层数解锁 → 玩家输出有成长
//   7) 击杀配额开门 → 刷怪有目标、有节奏
// ═══════════════════════════════════════════════════════════════

export const TILE = 32;
export const VIEW_W = 960;
export const VIEW_H = 540;
/** 一个屏幕能容纳多少瓦片（30 × 16.875 = 506.25） */
export const SCREEN_TILES = (VIEW_W / TILE) * (VIEW_H / TILE);

export function clamp(v: number, lo: number, hi: number): number {
  return Math.min(Math.max(v, lo), hi);
}

// ── 敌人数量：线性增长 + 软上限 ────────────────────────────────
export const ENEMY_BASE = 2;
export const ENEMY_PER_FLOOR = 1;
export const ENEMY_CAP = 60;         // 同层敌人总数上限（约 10~12 只同屏）

export function enemyCountForFloor(floor: number): number {
  return Math.min(ENEMY_BASE + ENEMY_PER_FLOOR * floor, ENEMY_CAP);
}

// ── 地图尺寸：面积 = 敌人数 × 每敌瓦片，再硬夹取 ────────────────
export const TPE_START = 300;        // 第 1 层每敌瓦片
export const TPE_MIN = 190;          // 每敌瓦片下限（越大越空旷）
export const TPE_DECAY = 8;          // 每层递减
export const MAP_ASPECT = 16 / 9;
export const MAP_MIN_W = 40;
export const MAP_MAX_W = 56;         // 世界 1792px ≈ 1.87 屏宽
export const MAP_MIN_H = 23;
export const MAP_MAX_H = 32;         // 世界 1024px ≈ 1.90 屏高

export function computeMapSize(floor: number): { w: number; h: number } {
  const n = enemyCountForFloor(floor);
  const tpe = Math.max(TPE_MIN, TPE_START - (floor - 1) * TPE_DECAY);
  const area = n * tpe;
  const h = clamp(Math.round(Math.sqrt(area / MAP_ASPECT)), MAP_MIN_H, MAP_MAX_H);
  const w = clamp(Math.round(h * MAP_ASPECT), MAP_MIN_W, MAP_MAX_W);
  return { w, h };
}

// ── 墙：边界墙 + 内部墙，内部墙数量封顶 ────────────────────────
// 小地图需要更多内部墙来增加策略性（不能只靠面积拉开难度）
export const WALL_RATIO = 0.06;      // 基础内部墙 = 瓦片数的 6%（从 5% 提升）
export const WALL_PER_FLOOR = 5;     // 每层额外内部墙（从 4 提升）
export const WALL_FLOOR_CAP = 60;    // 额外墙只累计到第 60 层（从 40 延长）
export const WALL_RATIO_CAP = 0.25;  // 墙占总瓦片比例硬上限（从 22% 提升到 25%）

export function interiorWallCount(floor: number, w: number, h: number): number {
  const tiles = w * h;
  const boundary = 2 * (w + h) - 4;
  const budget = Math.floor(tiles * WALL_RATIO_CAP) - boundary;
  const raw = Math.floor(tiles * WALL_RATIO) + Math.min(floor, WALL_FLOOR_CAP) * WALL_PER_FLOOR;
  return Math.max(0, Math.min(raw, budget, (w - 4) * (h - 4)));
}

// ── 敌人属性随楼层缩放 ────────────────────────────────────────
// HP 对数增长：低层平滑、高层不爆炸（1000 层 ≈ 4x，不会无限膨胀）
// 伤害封顶：高层威胁来自「同时贴脸的怪变多」，而不是「一刀秒杀」
// 速度封顶：保证仍可走位躲避
export const ENEMY_HP_GROWTH = 0.10;          // 对数增长系数
export const ENEMY_HP_CAP = 10.0;             // HP 最大倍率（约 2000 层达到）
export const ENEMY_DMG_GROWTH = 0.02;
export const ENEMY_DMG_CAP = 3.0;             // 伤害最大倍率（3x，不会一刀秒杀）
export const ENEMY_SPEED_GROWTH = 0.01;
export const ENEMY_SPEED_CAP = 1.3;           // 速度最多 1.3x，保证仍可走位躲避

/** 对数 HP 增长：floor=1→1x, 10→2x, 50→2.7x, 100→3x, 500→3.7x, 1000→4x */
export function enemyHpMul(floor: number): number {
  if (floor <= 1) return 1;
  const val = 1 + ENEMY_HP_GROWTH * Math.log2(floor) * 3;  // ×3 让增长更明显
  return Math.min(val, ENEMY_HP_CAP);
}

/** 线性伤害增长 + 硬上限 */
export function enemyDmgMul(floor: number): number {
  return Math.min(ENEMY_DMG_CAP, 1 + ENEMY_DMG_GROWTH * (floor - 1));
}

export function enemySpeedMul(floor: number): number {
  return Math.min(ENEMY_SPEED_CAP, 1 + ENEMY_SPEED_GROWTH * (floor - 1));
}

// ── 精英 / BOSS ───────────────────────────────────────────────
export const ELITE_CHANCE = 0.08;
export const ELITE_CHANCE_MAX = 0.25;
export const BOSS_EVERY = 10;        // 每 10 层一个 BOSS
// BOSS 强度倍率（相对基础怪）。HP 原为 14 倍 → 第 10 层约 7300 HP，
// 而玩家基础 DPS≈100 → 需 70 秒以上，体感「打不死」；下调为 8 倍。
export const BOSS_HP_MUL = 8;
export const BOSS_DMG_MUL = 1.7;
export const BOSS_SPEED_MUL = 0.92;

export function eliteChance(floor: number): number {
  return Math.min(ELITE_CHANCE_MAX, ELITE_CHANCE * (1 + floor * 0.01));
}

// ── 58 层后怪物词缀系统 ────────────────────────────────────────
// 58 层后（敌人数封顶），引入词缀增加多样性
export const AFFIX_START_FLOOR = 58;
export const AFFIX_CHANCE_BASE = 0.10;   // 基础词缀概率
export const AFFIX_CHANCE_PER_FLOOR = 0.005; // 每层额外 +0.5%

/** 词缀类型 */
export type AffixType = 'tanky' | 'fast' | 'regenerate' | 'splitter';

export interface AffixDef {
  type: AffixType;
  name: string;
  hpMul?: number;
  speedMul?: number;
  regenPerSec?: number;
  splitCount?: number;
}

/** 词缀定义表 */
export const AFFIX_TABLE: AffixDef[] = [
  { type: 'tanky', name: '铁甲', hpMul: 1.5, speedMul: 0.9 },
  { type: 'fast', name: '疾风', hpMul: 0.8, speedMul: 1.3 },
  { type: 'regenerate', name: '再生', hpMul: 1.0, regenPerSec: 2 },
  { type: 'splitter', name: '分裂', hpMul: 0.7, splitCount: 2 },
];

/** 获取当前楼层的词缀概率 */
export function affixChance(floor: number): number {
  if (floor < AFFIX_START_FLOOR) return 0;
  return Math.min(0.5, AFFIX_CHANCE_BASE + (floor - AFFIX_START_FLOOR) * AFFIX_CHANCE_PER_FLOOR);
}

/** 随机选择一个词缀 */
export function randomAffix(): AffixDef {
  return AFFIX_TABLE[Math.floor(Math.random() * AFFIX_TABLE.length)];
}

// ── 玩家天赋槽随层数解锁 ──────────────────────────────────────
/** 基础天赋槽数 */
export const TALENT_SLOTS_BASE = 3;
/** 每 20 层额外解锁 1 个天赋槽（上限 8 槽） */
export const TALENT_SLOT_PER_20_FLOOR = 1;
export const TALENT_SLOT_MAX = 8;

/** 获取当前层数的天赋槽数 */
export function talentSlotsForFloor(floor: number): number {
  const bonus = Math.floor((floor - 1) / 20);
  return Math.min(TALENT_SLOT_MAX, TALENT_SLOTS_BASE + bonus);
}

// ── 玩家射击 ──────────────────────────────────────────────────
/** 玩家子弹存活时长（ms） */
export const PLAYER_BULLET_LIFETIME_MS = 2200;
/** 玩家多重射击的散角（度） */
export const PLAYER_BULLET_SPREAD_DEG = 9;
